import { useState } from 'react';

interface UseDidChangeOptions<T> {
  initialCompareValue?: T;
  skipUpdate?: boolean;
}

/**
 * Usually, you will update state in event handlers.
 * However, in rare cases you might want to adjust state in response to rendering — for example,
 * you might want to change a state variable when a prop changes.
 *
 * https://react.dev/reference/react/useState#storing-information-from-previous-renders
 * https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
 */
export default function useDidChange<T>(
  value: T,
  options: UseDidChangeOptions<T> = {},
): boolean {
  const { initialCompareValue, skipUpdate } = options;
  const [prevValue, setPrevValue] = useState(() =>
    // This allows setting the initialCompareValue to undefined or null if desired
    'initialCompareValue' in options ? initialCompareValue : value,
  );

  if (prevValue !== value) {
    if (!skipUpdate) {
      setPrevValue(value);
    }

    return true;
  }

  return false;
}
